import { GridLegacy as Grid } from '@mui/material';
import { Form, Formio } from 'formiojs';
import { useSnackbar } from 'notistack';
import { memo, useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useHistory } from 'react-router-dom';

import { applyCustomFormioConfig } from '~/components/Forms/CustomBuilder';
import { formsCss } from '~/components/Forms/formCss';
import { FormsPageLoader } from '~/components/Forms/FormsPageLoader';
import { FormTemplateSelections } from '~/components/Forms/FormTemplateSelections';
import { preSubmitValidation } from '~/components/Forms/preSubmitValidation';
import { TemplateUpdateButtons } from '~/components/Forms/TemplateUpdateButtons';
import { useBootstrapJs } from '~/hooks/forms';
import { browserLogError } from '~/utils/helper';

import { formsRehireSubmit } from './data';
import { FormsRehire, useFormsRehire } from './useFormsRehire';
import { useStyles } from './useStyles';
declare module 'formiojs';

Formio.setBaseUrl(`${window.location.origin}/api/forms`);

applyCustomFormioConfig();

export const RehireEmployee = memo(() => {
  const formRef = useRef<HTMLDivElement>(null);
  const styles = useStyles();
  const { t } = useTranslation();
  const history = useHistory();
  const { enqueueSnackbar } = useSnackbar();

  const [formRootEl, setFormRootEl] = useState<HTMLElement>();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const {
    employeeId,
    formTemplates,
    selectedTemplateId,
    formConfigData,
    latestVersion,
    latestVersionLoading,
    formTranslations,
    existingData,
    rehireDataLoading,
    workflowConfig,
    existingCustomData,
    setSelectedTemplateId,
    refetchTemplateData,
  } = useFormsRehire();

  const onVersionUpdated = () => {
    if (refetchTemplateData) refetchTemplateData();
  };

  useBootstrapJs();

  useEffect(() => {
    let rehireForm: Form;

    if (
      formRootEl &&
      formConfigData &&
      selectedTemplateId &&
      formTranslations &&
      employeeId &&
      workflowConfig &&
      !rehireDataLoading
    ) {
      rehireForm = new Form(formRootEl, formConfigData, {
        ...formTranslations,
        language: formTranslations?.language,
      });

      rehireForm?.ready.then((inst) => {
        inst.submission = {
          data: { ...existingData, ...existingCustomData, workflowConfig },
        };

        inst.triggerChange();

        inst.on('cancel', () => inst.cancel());

        inst.on('goToPreviousPage', () => inst.prevPage());

        inst.on('goToNextPage', () => inst.nextPage());

        inst.on('customSubmit', async (data: FormsRehire) => {
          data.workHoursStartDate = data.employmentStartDate;
          const isValid = await preSubmitValidation(inst, data);
          if (!isValid) {
            // inst.submit() is called to trigger validation errors display
            inst.submit();
            return;
          }
          setIsSubmitting(true);
          formsRehireSubmit(selectedTemplateId, { data })
            .then((resp) => {
              inst.submit();
              setIsSubmitting(false);
              enqueueSnackbar(t('rehireSuccess'), { variant: 'success' });
              if (resp) {
                history.push(`/portal/employee/${employeeId}/people`);
                window.location.reload();
              }
            })
            .catch((err) => {
              setIsSubmitting(false);
              // Backend returns an already-translated message; show it as-is
              // and fall back to the generic label otherwise.
              enqueueSnackbar(err?.message || t('rehireError'), {
                variant: 'error',
              });
              browserLogError('Error on rehire submit:', err);
            });
        });
      });

      rehireForm.on(
        'error',
        (errors: Error) => {
          browserLogError('Form errors:', errors);
        },
        false,
      );
    }

    return () => {
      if (rehireForm) {
        rehireForm.off('submit');
      }
    };
  }, [
    formRef,
    formConfigData,
    selectedTemplateId,
    existingData,
    existingCustomData,
    formRootEl,
    formTranslations,
    employeeId,
    history,
    workflowConfig,
    rehireDataLoading,
    t,
    enqueueSnackbar,
  ]);

  const rootElCallbackRef = useCallback((node) => {
    if (node) {
      setFormRootEl(node);
    }
  }, []);

  return (
    <>
      <style>{formsCss}</style>
      <Grid container direction="column" style={styles.formPageContainer}>
        <h2 style={{ marginBottom: '50px' }}>{t('rehireEmployee')}</h2>

        <Grid item md={8} lg={6} style={styles.componentsContainer}>
          <Grid
            className="container-fluid"
            style={{ ...styles.container, maxWidth: '830px', marginLeft: 0 }}
          >
            <Grid style={styles.container} item>
              <FormTemplateSelections
                selectedTemplateId={selectedTemplateId ?? ''}
                formTemplates={formTemplates ?? []}
                onFormTemplateChange={setSelectedTemplateId}
              />
              <Link
                to={`/portal/employee/${employeeId}/people/basic?mode=rehire`}
                className="badge badge-info"
                style={styles.backToStandard}
              >
                {t('backToStandardRehireForm')}
              </Link>
            </Grid>
            <TemplateUpdateButtons
              updated={
                formConfigData?.settings?.version === latestVersion?.version
              }
              formId={formConfigData?._id || ''}
              disableAction={false}
              loading={latestVersionLoading}
              onVersionUpdated={onVersionUpdated}
            />
          </Grid>
        </Grid>

        <Grid item md={8} lg={6} style={styles.componentsContainer}>
          <Grid className="container-fluid" style={styles.container}>
            <div ref={rootElCallbackRef} />
          </Grid>
        </Grid>
      </Grid>
      <FormsPageLoader open={isSubmitting || rehireDataLoading} />
    </>
  );
});
RehireEmployee.displayName = 'RehireEmployee';
